import { supabase } from '@/lib/supabase';

/*
 * Affiliate program admin API (P5).
 *
 * All tables live on the shared dgxn project and are gated by `is_platform_admin()`
 * RLS. Nizan (nizan@otomator.co.il) qualifies, so the authenticated client reads and
 * writes directly — no Edge Function needed.
 *
 * Contract model (see PARTNER-AFFILIATE-PROGRAM-PLAN.md §10–11):
 *  - affiliate_partners       — partner identity only
 *  - affiliate_reward_templates + affiliate_template_lines — reusable versioned defaults
 *  - partner_agreements + partner_agreement_lines — per-partner effective-dated contracts
 *  - affiliate_links          — signup links, optionally bound to one agreement
 *  - partner_payouts          — monthly payout roll-up Nizan approves
 *
 * Effective-dating rule: never mutate a past agreement. Changing terms = insert a NEW
 * effective-dated agreement and mark the prior one `superseded`.
 */

export type RewardType = 'discount' | 'cash_payout';
export type PayoutMechanism = 'discount' | 'bank_transfer' | 'sumit' | 'offset_wa_invoice';
export type DurationType = 'fixed' | 'unlimited';
export type AgreementStatus = 'scheduled' | 'active' | 'superseded' | 'ended';
export type PayoutStatus = 'pending' | 'held' | 'requested' | 'approved' | 'paid' | 'invoiced';
export type RedemptionMethod = 'cash_payment_request' | 'ai_credits' | 'subscription_discount';
export type RedemptionStatus =
  | 'open'
  | 'submitted'
  | 'document_rejected'
  | 'fulfilled'
  | 'reset';
export type DiscountBalanceStatus = 'open' | 'exhausted' | 'held' | 'void';
export type PenaltyStatus = 'pending' | 'applied' | 'waived';

export const REWARD_TYPES: { value: RewardType; label: string }[] = [
  { value: 'cash_payout', label: 'Cash payout' },
  { value: 'discount', label: 'Discount (own subscription)' },
];

export const PAYOUT_MECHANISMS: { value: PayoutMechanism; label: string }[] = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'sumit', label: 'SUMIT payout' },
  { value: 'offset_wa_invoice', label: 'Offset WhatsApp invoice' },
  { value: 'discount', label: 'Discount on own subscription' },
];

export const DURATION_TYPES: { value: DurationType; label: string }[] = [
  { value: 'unlimited', label: 'While customer pays' },
  { value: 'fixed', label: 'Fixed number of months' },
];

/** Sentinel for the "(all products)" default line — a NULL product_code in the DB. */
export const ALL_PRODUCTS = '__all__';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AffiliatePartner {
  id: string;
  ext_partner_id: string | null;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  linked_org_id: string | null;
  is_active: boolean;
  created_at: string;
}

export interface PartnerListRow extends AffiliatePartner {
  current_reward_type: RewardType | null;
  current_payout_mechanism: PayoutMechanism | null;
  active_subs: number;
  mtd_earnings_agorot: number;
}

export interface RewardTemplateLine {
  id: string;
  template_id: string;
  system_code: string;
  product_code: string | null;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
  created_at: string;
}

export interface RewardTemplate {
  id: string;
  name: string;
  is_default: boolean;
  version: number;
  reward_type: RewardType;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
  payout_mechanism: PayoutMechanism;
  effective_from: string;
  is_active: boolean;
  supersedes_id: string | null;
  created_at: string;
}

export interface RewardTemplateWithLines extends RewardTemplate {
  lines: RewardTemplateLine[];
}

export interface AgreementLine {
  id: string;
  agreement_id: string;
  system_code: string;
  product_code: string | null;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
  created_at: string;
}

export interface PartnerAgreement {
  id: string;
  partner_id: string;
  template_id: string | null;
  reward_type: RewardType;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
  payout_mechanism: PayoutMechanism;
  effective_from: string;
  status: AgreementStatus;
  superseded_by: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface PartnerAgreementWithLines extends PartnerAgreement {
  lines: AgreementLine[];
}

export interface AffiliateLinkRow {
  id: string;
  code: string;
  token: string;
  label: string;
  partner_id: string | null;
  agreement_id: string | null;
  join_plan_code: string;
  join_free_days: number;
  reward_type: string;
  max_uses: number | null;
  use_count: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

export interface PartnerPayout {
  id: string;
  partner_id: string;
  agreement_id: string | null;
  period: string;
  gross_customer_paid_agorot: number;
  net_customer_paid_agorot: number | null;
  commission_pct: number;
  amount_agorot: number;
  amount_net_agorot: number | null;
  vat_percent: number | null;
  vat_agorot: number | null;
  amount_gross_agorot: number | null;
  active_subs_count: number;
  status: PayoutStatus;
  approved_by: string | null;
  approved_at: string | null;
  paid_at: string | null;
  paid_reference: string | null;
  invoice_requested_at: string | null;
  invoice_received_at: string | null;
  invoice_number: string | null;
  invoice_url: string | null;
  invoice_note: string | null;
  redemption_method: RedemptionMethod | null;
  redemption_status: RedemptionStatus;
  redeemed_at: string | null;
  redemption_ref_type: string | null;
  redemption_ref_id: string | null;
  external_doc_url: string | null;
  external_doc_number: string | null;
  external_doc_r2_key: string | null;
  external_doc_rejected_reason: string | null;
  receipt_due_at: string | null;
  receipt_r2_key: string | null;
  commitment_text: string | null;
  commitment_accepted_at: string | null;
  entity_kind_snapshot: string | null;
  reset_at: string | null;
  reset_reason: string | null;
  created_at: string;
}

export interface DiscountBalanceRow {
  id: string;
  payout_id: string;
  status: DiscountBalanceStatus;
  granted_agorot: number;
  remaining_agorot: number;
}

export interface PartnerPenalty {
  id: string;
  partner_id: string;
  payout_id: string;
  penalty_agorot: number;
  reason: string;
  status: PenaltyStatus;
  applied_to_payout_id: string | null;
  waived_by: string | null;
  waived_reason: string | null;
  created_at: string;
}

export interface PartnerPenaltyRow extends PartnerPenalty {
  partner_name: string;
}

export interface PayoutRow extends PartnerPayout {
  partner_name: string;
  discount_balance: DiscountBalanceRow | null;
  /** Paid cash payout past receipt_due_at with no receipt yet. */
  receipt_late: boolean;
  /** Blocked from mark-paid because partner has a prior unpaid-for cash receipt. */
  held_by_late_receipt: boolean;
  /** Discount balance left in held/conflict after a provider-called failed charge. */
  discount_held: boolean;
}

export interface MarkPaidInput {
  paid_at: string;
  reference: string;
}

export interface CreditTransaction {
  id: string;
  org_id: string;
  referral_id: string | null;
  amount_agorot: number;
  type: string;
  description: string | null;
  period: string | null;
  created_at: string;
}

export interface ReferredCustomer {
  referral_id: string;
  org_id: string;
  org_name: string;
  org_status: string | null;
  current_plan: string | null;
  last_monthly_paid_agorot: number | null;
  referral_status: string;
  joined_at: string;
  total_earned_agorot: number;
}

export interface ProductOption {
  system_code: string;
  code: string;
  label: string;
  kind: 'plan' | 'addon';
}

// Line shape used by create/update forms (no ids yet).
export interface LineInput {
  system_code: string;
  product_code: string | null;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let cachedUserId: string | null = null;

/** Resolve the current admin's `users.id` (for approved_by / created_by audit). */
async function currentUserId(): Promise<string | null> {
  if (cachedUserId) return cachedUserId;
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return null;
  const { data } = await supabase
    .from('users')
    .select('id')
    .eq('auth_user_id', auth.user.id)
    .maybeSingle();
  cachedUserId = (data?.id as string) ?? null;
  return cachedUserId;
}

function firstOfCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

/** Today as an ISO date (YYYY-MM-DD) in local time. */
export function todayISODate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

/** Shareable partner trial URL. */
export function startUrl(code: string): string {
  return `https://hub.jumpstart.co.il/start?ref=${encodeURIComponent(code)}`;
}

/** Zero-dependency QR image (external renderer) for a given payload. */
export function qrImageUrl(payload: string, size = 180): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    payload,
  )}`;
}

const CODE_RE = /^[A-Z0-9]{4,20}$/;

function normalizeCode(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!CODE_RE.test(normalized)) {
    throw new Error('Code must be 4–20 uppercase letters or digits');
  }
  return normalized;
}

// ---------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------

export interface CreatePartnerInput {
  name: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  ext_partner_id?: string | null;
  linked_org_id?: string | null;
}

export type UpdatePartnerPatch = Partial<CreatePartnerInput> & { is_active?: boolean };

const partnersApi = {
  list: async (): Promise<PartnerListRow[]> => {
    const { data, error } = await supabase
      .from('affiliate_partners')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    const partners = (data ?? []) as AffiliatePartner[];
    if (!partners.length) return [];

    const partnerIds = partners.map((p) => p.id);

    // Active agreement per partner (for reward_type / payout_mechanism display).
    const { data: agrData, error: agrErr } = await supabase
      .from('partner_agreements')
      .select('partner_id, reward_type, payout_mechanism, effective_from, status')
      .in('partner_id', partnerIds)
      .eq('status', 'active');
    if (agrErr) throw agrErr;
    const agrByPartner = new Map<string, { reward_type: RewardType; payout_mechanism: PayoutMechanism }>();
    for (const a of (agrData ?? []) as PartnerAgreement[]) {
      // Latest active wins if multiple.
      const prev = agrByPartner.get(a.partner_id);
      if (!prev) agrByPartner.set(a.partner_id, { reward_type: a.reward_type, payout_mechanism: a.payout_mechanism });
    }

    // Referrals → active subs + referral→partner map for MTD earnings.
    const { data: refData, error: refErr } = await supabase
      .from('affiliate_referrals')
      .select('id, partner_id, status')
      .in('partner_id', partnerIds);
    if (refErr) throw refErr;
    const activeByPartner = new Map<string, number>();
    const partnerByReferral = new Map<string, string>();
    for (const r of (refData ?? []) as { id: string; partner_id: string; status: string }[]) {
      partnerByReferral.set(r.id, r.partner_id);
      if (r.status === 'active') activeByPartner.set(r.partner_id, (activeByPartner.get(r.partner_id) ?? 0) + 1);
    }

    // MTD earnings: credit transactions this month for those referrals.
    const mtdByPartner = new Map<string, number>();
    const referralIds = [...partnerByReferral.keys()];
    if (referralIds.length) {
      const { data: txData, error: txErr } = await supabase
        .from('affiliate_credit_transactions')
        .select('referral_id, amount_agorot, period')
        .in('referral_id', referralIds)
        .gte('period', firstOfCurrentMonth());
      if (txErr) throw txErr;
      for (const tx of (txData ?? []) as { referral_id: string | null; amount_agorot: number }[]) {
        if (!tx.referral_id) continue;
        const pid = partnerByReferral.get(tx.referral_id);
        if (!pid) continue;
        mtdByPartner.set(pid, (mtdByPartner.get(pid) ?? 0) + (tx.amount_agorot ?? 0));
      }
    }

    return partners.map((p) => ({
      ...p,
      current_reward_type: agrByPartner.get(p.id)?.reward_type ?? null,
      current_payout_mechanism: agrByPartner.get(p.id)?.payout_mechanism ?? null,
      active_subs: activeByPartner.get(p.id) ?? 0,
      mtd_earnings_agorot: mtdByPartner.get(p.id) ?? 0,
    }));
  },

  get: async (id: string): Promise<AffiliatePartner> => {
    const { data, error } = await supabase.from('affiliate_partners').select('*').eq('id', id).single();
    if (error) throw error;
    return data as AffiliatePartner;
  },

  create: async (input: CreatePartnerInput): Promise<AffiliatePartner> => {
    if (!input.name.trim()) throw new Error('Name is required');
    const row = {
      name: input.name.trim(),
      contact_email: input.contact_email?.trim() || null,
      contact_phone: input.contact_phone?.trim() || null,
      ext_partner_id: input.ext_partner_id || null,
      linked_org_id: input.linked_org_id || null,
      is_active: true,
    };
    const { data, error } = await supabase.from('affiliate_partners').insert(row).select('*').single();
    if (error) throw error;
    return data as AffiliatePartner;
  },

  update: async (id: string, patch: UpdatePartnerPatch): Promise<AffiliatePartner> => {
    const updates: Record<string, unknown> = {};
    if (patch.name !== undefined) updates.name = patch.name.trim();
    if (patch.contact_email !== undefined) updates.contact_email = patch.contact_email?.trim() || null;
    if (patch.contact_phone !== undefined) updates.contact_phone = patch.contact_phone?.trim() || null;
    if (patch.ext_partner_id !== undefined) updates.ext_partner_id = patch.ext_partner_id || null;
    if (patch.linked_org_id !== undefined) updates.linked_org_id = patch.linked_org_id || null;
    if (patch.is_active !== undefined) updates.is_active = patch.is_active;
    const { data, error } = await supabase
      .from('affiliate_partners')
      .update(updates)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    return data as AffiliatePartner;
  },
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export interface TemplateHeaderInput {
  name: string;
  reward_type: RewardType;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
  payout_mechanism: PayoutMechanism;
  is_default: boolean;
  effective_from: string;
}

async function attachTemplateLines(templates: RewardTemplate[]): Promise<RewardTemplateWithLines[]> {
  if (!templates.length) return [];
  const ids = templates.map((t) => t.id);
  const { data, error } = await supabase
    .from('affiliate_template_lines')
    .select('*')
    .in('template_id', ids)
    .order('product_code', { ascending: true, nullsFirst: true });
  if (error) throw error;
  const byTemplate = new Map<string, RewardTemplateLine[]>();
  for (const line of (data ?? []) as RewardTemplateLine[]) {
    const list = byTemplate.get(line.template_id) ?? [];
    list.push(line);
    byTemplate.set(line.template_id, list);
  }
  return templates.map((t) => ({ ...t, lines: byTemplate.get(t.id) ?? [] }));
}

const templatesApi = {
  list: async (): Promise<RewardTemplateWithLines[]> => {
    const { data, error } = await supabase
      .from('affiliate_reward_templates')
      .select('*')
      .order('name', { ascending: true })
      .order('version', { ascending: false });
    if (error) throw error;
    return attachTemplateLines((data ?? []) as RewardTemplate[]);
  },

  getWithLines: async (id: string): Promise<RewardTemplateWithLines> => {
    const { data, error } = await supabase
      .from('affiliate_reward_templates')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    const [withLines] = await attachTemplateLines([data as RewardTemplate]);
    return withLines;
  },

  /**
   * Create a new template — either a brand new one or a new version that supersedes
   * an existing template row. Setting is_default clears any other default first.
   */
  createVersion: async (
    input: TemplateHeaderInput,
    lines: LineInput[],
    supersedesId?: string | null,
  ): Promise<RewardTemplate> => {
    if (!input.name.trim()) throw new Error('Template name is required');
    if (input.duration_type === 'fixed' && !input.duration_months) {
      throw new Error('Fixed duration requires a month count');
    }

    let version = 1;
    if (supersedesId) {
      const { data: prev, error: prevErr } = await supabase
        .from('affiliate_reward_templates')
        .select('version')
        .eq('id', supersedesId)
        .single();
      if (prevErr) throw prevErr;
      version = ((prev?.version as number) ?? 0) + 1;
    }

    if (input.is_default) {
      const { error: clearErr } = await supabase
        .from('affiliate_reward_templates')
        .update({ is_default: false })
        .eq('is_default', true);
      if (clearErr) throw clearErr;
    }

    const { data, error } = await supabase
      .from('affiliate_reward_templates')
      .insert({
        name: input.name.trim(),
        is_default: input.is_default,
        version,
        reward_type: input.reward_type,
        commission_pct: input.commission_pct,
        min_active_subs: input.min_active_subs,
        duration_type: input.duration_type,
        duration_months: input.duration_type === 'fixed' ? input.duration_months : null,
        payout_mechanism: input.payout_mechanism,
        effective_from: input.effective_from,
        is_active: true,
        supersedes_id: supersedesId || null,
      })
      .select('*')
      .single();
    if (error) throw error;
    const template = data as RewardTemplate;

    // Mark the superseded template inactive.
    if (supersedesId) {
      await supabase
        .from('affiliate_reward_templates')
        .update({ is_active: false })
        .eq('id', supersedesId);
    }

    await templatesApi.replaceLines(template.id, lines);
    return template;
  },

  setDefault: async (id: string): Promise<void> => {
    const { error: clearErr } = await supabase
      .from('affiliate_reward_templates')
      .update({ is_default: false })
      .eq('is_default', true);
    if (clearErr) throw clearErr;
    const { error } = await supabase
      .from('affiliate_reward_templates')
      .update({ is_default: true })
      .eq('id', id);
    if (error) throw error;
  },

  /** Replace all per-product lines for a template. */
  replaceLines: async (templateId: string, lines: LineInput[]): Promise<void> => {
    const { error: delErr } = await supabase
      .from('affiliate_template_lines')
      .delete()
      .eq('template_id', templateId);
    if (delErr) throw delErr;
    if (!lines.length) return;
    const rows = lines.map((l) => ({
      template_id: templateId,
      system_code: l.system_code,
      product_code: l.product_code,
      commission_pct: l.commission_pct,
      min_active_subs: l.min_active_subs,
      duration_type: l.duration_type,
      duration_months: l.duration_type === 'fixed' ? l.duration_months : null,
    }));
    const { error } = await supabase.from('affiliate_template_lines').insert(rows);
    if (error) throw error;
  },
};

// ---------------------------------------------------------------------------
// Agreements
// ---------------------------------------------------------------------------

export interface CreateAgreementInput {
  template_id?: string | null;
  reward_type: RewardType;
  commission_pct: number;
  min_active_subs: number;
  duration_type: DurationType;
  duration_months: number | null;
  payout_mechanism: PayoutMechanism;
  effective_from: string;
  notes?: string | null;
}

async function attachAgreementLines(
  agreements: PartnerAgreement[],
): Promise<PartnerAgreementWithLines[]> {
  if (!agreements.length) return [];
  const ids = agreements.map((a) => a.id);
  const { data, error } = await supabase
    .from('partner_agreement_lines')
    .select('*')
    .in('agreement_id', ids)
    .order('product_code', { ascending: true, nullsFirst: true });
  if (error) throw error;
  const byAgreement = new Map<string, AgreementLine[]>();
  for (const line of (data ?? []) as AgreementLine[]) {
    const list = byAgreement.get(line.agreement_id) ?? [];
    list.push(line);
    byAgreement.set(line.agreement_id, list);
  }
  return agreements.map((a) => ({ ...a, lines: byAgreement.get(a.id) ?? [] }));
}

const agreementsApi = {
  listByPartner: async (partnerId: string): Promise<PartnerAgreementWithLines[]> => {
    const { data, error } = await supabase
      .from('partner_agreements')
      .select('*')
      .eq('partner_id', partnerId)
      .order('effective_from', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) throw error;
    return attachAgreementLines((data ?? []) as PartnerAgreement[]);
  },

  /**
   * Insert a new effective-dated agreement and supersede prior active/scheduled ones.
   * Never mutates a past agreement in place.
   */
  create: async (
    partnerId: string,
    input: CreateAgreementInput,
    lines: LineInput[],
  ): Promise<PartnerAgreement> => {
    if (input.duration_type === 'fixed' && !input.duration_months) {
      throw new Error('Fixed duration requires a month count');
    }

    const createdBy = await currentUserId();
    const { data, error } = await supabase
      .from('partner_agreements')
      .insert({
        partner_id: partnerId,
        template_id: input.template_id || null,
        reward_type: input.reward_type,
        commission_pct: input.commission_pct,
        min_active_subs: input.min_active_subs,
        duration_type: input.duration_type,
        duration_months: input.duration_type === 'fixed' ? input.duration_months : null,
        payout_mechanism: input.payout_mechanism,
        effective_from: input.effective_from,
        status: 'active',
        notes: input.notes?.trim() || null,
        created_by: createdBy,
      })
      .select('*')
      .single();
    if (error) {
      if ((error as { code?: string }).code === '23505') {
        throw new Error('An agreement with that effective date already exists for this partner');
      }
      throw error;
    }
    const agreement = data as PartnerAgreement;

    // Supersede any other active/scheduled agreement for this partner.
    const { error: supErr } = await supabase
      .from('partner_agreements')
      .update({ status: 'superseded', superseded_by: agreement.id })
      .eq('partner_id', partnerId)
      .in('status', ['active', 'scheduled'])
      .neq('id', agreement.id);
    if (supErr) throw supErr;

    // Insert per-product lines.
    if (lines.length) {
      const rows = lines.map((l) => ({
        agreement_id: agreement.id,
        system_code: l.system_code,
        product_code: l.product_code,
        commission_pct: l.commission_pct,
        min_active_subs: l.min_active_subs,
        duration_type: l.duration_type,
        duration_months: l.duration_type === 'fixed' ? l.duration_months : null,
      }));
      const { error: lineErr } = await supabase.from('partner_agreement_lines').insert(rows);
      if (lineErr) throw lineErr;
    }

    return agreement;
  },
};

// ---------------------------------------------------------------------------
// Signup links
// ---------------------------------------------------------------------------

export interface CreateLinkInput {
  label: string;
  code: string;
  partner_id: string;
  agreement_id?: string | null;
  join_plan_code?: string;
  join_free_days?: number;
  max_uses?: number | null;
  expires_at?: string | null;
}

const linksApi = {
  listByPartner: async (partnerId: string): Promise<AffiliateLinkRow[]> => {
    const { data, error } = await supabase
      .from('affiliate_links')
      .select('*')
      .eq('partner_id', partnerId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as AffiliateLinkRow[];
  },

  create: async (input: CreateLinkInput): Promise<AffiliateLinkRow> => {
    const code = normalizeCode(input.code);
    const { data: existing, error: dupErr } = await supabase
      .from('affiliate_links')
      .select('id')
      .eq('code', code)
      .maybeSingle();
    if (dupErr) throw dupErr;
    if (existing) throw new Error('Code already in use');

    const row = {
      label: input.label.trim(),
      code,
      partner_id: input.partner_id,
      agreement_id: input.agreement_id || null,
      join_plan_code: input.join_plan_code ?? 'ignite',
      join_free_days: input.join_free_days ?? 30,
      reward_type: 'none',
      max_uses: input.max_uses ?? null,
      expires_at: input.expires_at || null,
      is_active: true,
    };
    const { data, error } = await supabase.from('affiliate_links').insert(row).select('*').single();
    if (error) throw error;
    return data as AffiliateLinkRow;
  },

  toggleActive: async (id: string, isActive: boolean): Promise<void> => {
    const { error } = await supabase.from('affiliate_links').update({ is_active: isActive }).eq('id', id);
    if (error) throw error;
  },
};

// ---------------------------------------------------------------------------
// Payouts
// ---------------------------------------------------------------------------

const REWARD_LABEL: Record<RedemptionMethod, string> = {
  cash_payment_request: 'Payment request',
  ai_credits: 'AI credits',
  subscription_discount: 'Subscription discount',
};

export function redemptionMethodLabel(method: RedemptionMethod | null): string {
  if (!method) return 'Not chosen';
  return REWARD_LABEL[method] ?? method;
}

/** Map admin-payout RPC refusals to readable English. */
export function mapPayoutAdminError(error: { message?: string; code?: string } | null | undefined): string {
  const raw = (error?.message ?? '').trim();
  const lower = raw.toLowerCase();
  const code = (error?.code ?? '').toUpperCase();

  if (
    code === '42501' ||
    lower.includes('unauthorized') ||
    lower.includes('forbidden') ||
    lower.includes('permission denied') ||
    lower.includes('not authorized')
  ) {
    return 'Permission denied — sign in as a platform admin.';
  }

  const known: Array<[RegExp | string, string]> = [
    ['wrong_redemption_method', 'This payout is not a cash payment request — only cash payouts can be marked paid.'],
    ['already_paid', 'This payout is already marked paid.'],
    ['payout_not_ready', 'This payout is not ready to mark paid yet.'],
    ['document_rejected', 'The submitted document was rejected — the partner must resubmit before you can mark paid.'],
    ['payment_request_required', 'A payment-request document is required before marking paid.'],
    [
      'Partner has an unpaid-for invoice from a previous payout',
      'Held — this partner still owes a receipt from a previous paid payout.',
    ],
    ['invalid_rejection_state', 'This payout cannot have its document rejected in the current state.'],
    ['reason_required', 'A reason is required.'],
    ['discount_already_consumed', 'Cannot reset — part of the discount balance was already consumed.'],
    ['credits_already_consumed', 'Cannot reset — AI credits from this payout were already used.'],
    ['pending_penalty_not_found', 'No pending penalty found to waive.'],
    ['payout_not_found', 'Payout not found.'],
  ];

  for (const [needle, message] of known) {
    if (typeof needle === 'string') {
      if (raw.includes(needle) || lower.includes(needle.toLowerCase())) return message;
    } else if (needle.test(raw)) {
      return message;
    }
  }

  if (!raw) return 'Action failed.';
  // Strip PostgREST noise wrappers when present.
  const cleaned = raw.replace(/^.*?:\s*/, '').replace(/\n.*/s, '').trim();
  return cleaned || raw;
}

function throwMapped(error: { message?: string; code?: string }): never {
  throw new Error(mapPayoutAdminError(error));
}

function isReceiptLate(p: PartnerPayout, now = Date.now()): boolean {
  if (p.redemption_method !== 'cash_payment_request') return false;
  if (!p.paid_at || p.invoice_received_at) return false;
  if (!p.receipt_due_at) return false;
  return new Date(p.receipt_due_at).getTime() < now;
}

function partnerHasLateUnpaidReceipt(all: PartnerPayout[], partnerId: string, now = Date.now()): boolean {
  return all.some(
    (p) =>
      p.partner_id === partnerId &&
      p.redemption_method === 'cash_payment_request' &&
      !!p.paid_at &&
      !p.invoice_received_at &&
      (!!p.receipt_due_at ? new Date(p.receipt_due_at).getTime() < now : true),
  );
}

type PayoutListDbRow = PartnerPayout & {
  affiliate_partners?: { id: string; name: string } | { id: string; name: string }[] | null;
  partner_discount_balances?:
    | DiscountBalanceRow
    | DiscountBalanceRow[]
    | null;
};

function normalizePayoutRows(rows: PayoutListDbRow[]): PayoutRow[] {
  const now = Date.now();
  const base = rows.map((row) => {
    const partner = Array.isArray(row.affiliate_partners)
      ? row.affiliate_partners[0]
      : row.affiliate_partners;
    const balances = row.partner_discount_balances;
    const balance = Array.isArray(balances) ? balances[0] ?? null : balances ?? null;
    const { affiliate_partners: _p, partner_discount_balances: _b, ...payout } = row;
    return {
      ...(payout as PartnerPayout),
      partner_name: partner?.name ?? '—',
      discount_balance: balance,
      receipt_late: false,
      held_by_late_receipt: false,
      discount_held: balance?.status === 'held',
    } satisfies PayoutRow;
  });

  return base.map((row) => {
    const receiptLate = isReceiptLate(row, now);
    const partnerLate = partnerHasLateUnpaidReceipt(base, row.partner_id, now);
    const unpaid = row.status !== 'paid' && row.status !== 'invoiced';
    return {
      ...row,
      receipt_late: receiptLate,
      held_by_late_receipt: unpaid && partnerLate && !receiptLate,
    };
  });
}

const payoutsApi = {
  listByPeriod: async (period?: string): Promise<PayoutRow[]> => {
    let query = supabase
      .from('partner_payouts')
      .select(
        '*, affiliate_partners(id, name), partner_discount_balances(id, payout_id, status, granted_agorot, remaining_agorot)',
      )
      .order('period', { ascending: false })
      .order('created_at', { ascending: false });
    if (period) query = query.eq('period', period);
    const { data, error } = await query;
    if (error) throwMapped(error);
    return normalizePayoutRows((data ?? []) as PayoutListDbRow[]);
  },

  listByPartner: async (partnerId: string): Promise<PartnerPayout[]> => {
    const { data, error } = await supabase
      .from('partner_payouts')
      .select('*')
      .eq('partner_id', partnerId)
      .order('period', { ascending: false });
    if (error) throwMapped(error);
    return (data ?? []) as PartnerPayout[];
  },

  /** Distinct periods present, for the payouts filter. */
  listPeriods: async (): Promise<string[]> => {
    const { data, error } = await supabase
      .from('partner_payouts')
      .select('period')
      .order('period', { ascending: false });
    if (error) throwMapped(error);
    return [...new Set((data ?? []).map((r: { period: string }) => r.period))];
  },

  listPendingPenalties: async (): Promise<PartnerPenaltyRow[]> => {
    const { data, error } = await supabase
      .from('partner_penalties')
      .select('*, affiliate_partners(id, name)')
      .eq('status', 'pending')
      .order('created_at', { ascending: true });
    if (error) throwMapped(error);
    return ((data ?? []) as Array<PartnerPenalty & { affiliate_partners?: { name: string } | { name: string }[] | null }>).map(
      (row) => {
        const partner = Array.isArray(row.affiliate_partners)
          ? row.affiliate_partners[0]
          : row.affiliate_partners;
        const { affiliate_partners: _p, ...penalty } = row;
        return { ...(penalty as PartnerPenalty), partner_name: partner?.name ?? '—' };
      },
    );
  },

  markPaid: async (id: string, input: MarkPaidInput): Promise<void> => {
    const { error } = await supabase.rpc('admin_mark_payout_paid', {
      p_payout_id: id,
      p_paid_at: input.paid_at,
      p_reference: input.reference.trim() || null,
    });
    if (error) throwMapped(error);
  },

  rejectDocument: async (id: string, reason: string): Promise<void> => {
    const { error } = await supabase.rpc('admin_reject_payout_document', {
      p_payout_id: id,
      p_reason: reason.trim(),
    });
    if (error) throwMapped(error);
  },

  resetRedemption: async (id: string, reason: string): Promise<void> => {
    const { error } = await supabase.rpc('admin_reset_payout_redemption', {
      p_payout_id: id,
      p_reason: reason.trim(),
    });
    if (error) throwMapped(error);
  },

  waivePenalty: async (penaltyId: string, reason: string): Promise<void> => {
    const { error } = await supabase.rpc('admin_waive_partner_penalty', {
      p_penalty_id: penaltyId,
      p_reason: reason.trim(),
    });
    if (error) throwMapped(error);
  },
};

// ---------------------------------------------------------------------------
// Earnings + customers (read-only)
// ---------------------------------------------------------------------------

const earningsApi = {
  creditTransactionsByPartner: async (partnerId: string): Promise<CreditTransaction[]> => {
    const { data: refs, error: refErr } = await supabase
      .from('affiliate_referrals')
      .select('id')
      .eq('partner_id', partnerId);
    if (refErr) throw refErr;
    const referralIds = (refs ?? []).map((r: { id: string }) => r.id);
    if (!referralIds.length) return [];
    const { data, error } = await supabase
      .from('affiliate_credit_transactions')
      .select('*')
      .in('referral_id', referralIds)
      .order('period', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as CreditTransaction[];
  },
};

const customersApi = {
  listByPartner: async (partnerId: string): Promise<ReferredCustomer[]> => {
    const { data: refs, error: refErr } = await supabase
      .from('affiliate_referrals')
      .select('id, referred_org_id, status, created_at, total_earned_agorot')
      .eq('partner_id', partnerId)
      .order('created_at', { ascending: false });
    if (refErr) throw refErr;
    const referrals = (refs ?? []) as {
      id: string;
      referred_org_id: string;
      status: string;
      created_at: string;
      total_earned_agorot: number;
    }[];
    if (!referrals.length) return [];

    const orgIds = [...new Set(referrals.map((r) => r.referred_org_id))];
    const [orgRes, subRes] = await Promise.all([
      supabase.from('organizations').select('id, name, status, plan_code').in('id', orgIds),
      supabase
        .from('org_system_subscriptions')
        .select('organization_id, plan_code, effective_price_agorot, price_agorot, status')
        .in('organization_id', orgIds)
        .eq('system_code', 'jumpstart'),
    ]);
    if (orgRes.error) throw orgRes.error;
    if (subRes.error) throw subRes.error;

    const orgById = new Map(
      (orgRes.data ?? []).map((o: { id: string; name: string; status: string | null; plan_code: string | null }) => [
        o.id,
        o,
      ]),
    );
    const subByOrg = new Map<string, { plan_code: string | null; paid: number | null }>();
    for (const s of (subRes.data ?? []) as {
      organization_id: string;
      plan_code: string | null;
      effective_price_agorot: number | null;
      price_agorot: number | null;
      status: string | null;
    }[]) {
      // Prefer an active subscription; otherwise keep the first seen.
      if (!subByOrg.has(s.organization_id) || s.status === 'active') {
        subByOrg.set(s.organization_id, {
          plan_code: s.plan_code,
          paid: s.effective_price_agorot ?? s.price_agorot ?? null,
        });
      }
    }

    return referrals.map((r) => {
      const org = orgById.get(r.referred_org_id);
      const sub = subByOrg.get(r.referred_org_id);
      return {
        referral_id: r.id,
        org_id: r.referred_org_id,
        org_name: org?.name ?? '—',
        org_status: org?.status ?? null,
        current_plan: sub?.plan_code ?? org?.plan_code ?? null,
        last_monthly_paid_agorot: sub?.paid ?? null,
        referral_status: r.status,
        joined_at: r.created_at,
        total_earned_agorot: r.total_earned_agorot ?? 0,
      };
    });
  },
};

// ---------------------------------------------------------------------------
// Catalog (products for pickers)
// ---------------------------------------------------------------------------

const catalogApi = {
  listPlans: async (): Promise<{ system_code: string; code: string; name: string }[]> => {
    const { data, error } = await supabase
      .from('system_license_plans')
      .select('system_code, code, name, sort_order')
      .eq('is_active', true)
      .order('system_code', { ascending: true })
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return (data ?? []) as { system_code: string; code: string; name: string }[];
  },

  listAddons: async (): Promise<{ system_code: string | null; code: string; name: string }[]> => {
    const { data, error } = await supabase
      .from('addon_catalog')
      .select('system_code, code, name, sort_order')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return (data ?? []) as { system_code: string | null; code: string; name: string }[];
  },

  /** Combined product options for line pickers. */
  productOptions: async (): Promise<ProductOption[]> => {
    const [plans, addons] = await Promise.all([catalogApi.listPlans(), catalogApi.listAddons()]);
    const planOpts: ProductOption[] = plans.map((p) => ({
      system_code: p.system_code,
      code: p.code,
      label: `${p.system_code}/${p.code} — ${p.name}`,
      kind: 'plan',
    }));
    const addonOpts: ProductOption[] = addons.map((a) => ({
      system_code: a.system_code ?? 'jumpstart',
      code: a.code,
      label: `${a.system_code ?? 'jumpstart'}/${a.code} — ${a.name} (add-on)`,
      kind: 'addon',
    }));
    return [...planOpts, ...addonOpts];
  },
};

export const affiliateApi = {
  partners: partnersApi,
  templates: templatesApi,
  agreements: agreementsApi,
  links: linksApi,
  payouts: payoutsApi,
  earnings: earningsApi,
  customers: customersApi,
  catalog: catalogApi,
};
